'use client'

import { usePathname } from 'next/navigation'
import { useEffect } from 'react'

/**
 * Keeps `<html lang>` and `<html dir>` in step with the current route.
 *
 * The root layout already sets both on the server from the `x-pathname` header
 * forwarded by `src/proxy.ts`, which is correct for any full page load. It is
 * not enough on its own: a client-side transition reuses the same document, so
 * navigating from an Arabic route to an English one would leave the English
 * page rendering `dir="rtl"` — every layout mirrored, for the rest of the
 * session.
 *
 * Links across the language boundary are plain `<a>` document navigations for
 * exactly that reason. This component is the backstop that makes the whole
 * category impossible rather than depending on every future link being written
 * correctly: whatever route the client router lands on, direction follows.
 *
 * RTL is Arabic-only. Nothing else on the site may inherit it.
 */

const ARABIC_PREFIX = '/ar'

export default function DocumentLanguage() {
  const pathname = usePathname()

  useEffect(() => {
    const isArabic =
      pathname === ARABIC_PREFIX || pathname?.startsWith(`${ARABIC_PREFIX}/`)

    const root = document.documentElement
    root.lang = isArabic ? 'ar' : 'en'

    if (isArabic) {
      root.dir = 'rtl'
    } else {
      // removeAttribute, not dir='ltr': the English tree ships no dir at all,
      // so this restores the exact server-rendered state rather than a
      // near-equivalent.
      root.removeAttribute('dir')
    }
  }, [pathname])

  return null
}
